/**
 * Lazy Video Loading
 * Loads videos only when they enter the viewport
 */

export class LazyVideo {
  constructor() {
    this.videos = document.querySelectorAll('[data-video-lazy-load="true"]');
    this.observerOptions = {
      root: null,
      rootMargin: '100px',
      threshold: 0
    };
    
    this.init();
  }

  init() {
    if (!this.videos.length) return;

    // Use Intersection Observer if supported
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        this.observerOptions
      );

      this.videos.forEach(video => {
        this.observer.observe(video);
      });
    } else {
      // Fallback: load all videos immediately for browsers without IntersectionObserver
      this.videos.forEach(video => this.loadVideo(video));
    }
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const videoElement = entry.target;
        
        // Check if video hasn't been loaded yet
        if (videoElement.dataset.videoLoaded === 'false') {
          this.loadVideo(videoElement);
          this.observer.unobserve(videoElement);
        }
      }
    });
  }

  loadVideo(videoElement) {
    const template = videoElement.querySelector('template');
    
    if (!template) return;

    // Mark as loaded to prevent duplicate loading
    videoElement.dataset.videoLoaded = 'true';

    // Get the content from template
    const content = template.content.cloneNode(true);
    
    // Check if video should autoplay
    const shouldAutoplay = videoElement.hasAttribute('autoplay');
    
    // Insert the video after the template
    template.after(content);

    // If autoplay is enabled, trigger play
    if (shouldAutoplay) {
      this.handleAutoplay(videoElement);
    }

    // Dispatch custom event for other scripts that might need to know video was loaded
    videoElement.dispatchEvent(new CustomEvent('video:loaded', {
      bubbles: true,
      detail: { element: videoElement }
    }));
  }

  handleAutoplay(videoElement) {
    setTimeout(() => {
      const iframe = videoElement.querySelector('iframe');
      const video = videoElement.querySelector('video');

      if (iframe) {
        // For YouTube/Vimeo iframes, the autoplay is handled via URL parameters
        // Just ensure the iframe is loaded
        console.log('Iframe video loaded with autoplay');
      } else if (video) {
        // For native HTML5 video
        video.play().catch(error => {
          console.warn('Autoplay failed:', error);
        });
      }
    }, 100);
  }

  // Method to manually load a specific video (useful for programmatic control)
  static loadVideoById(elementId) {
    const videoElement = document.getElementById(elementId);
    if (videoElement && videoElement.dataset.videoLoaded === 'false') {
      const lazyVideo = new LazyVideo();
      lazyVideo.loadVideo(videoElement);
    }
  }

  // Method to load all videos immediately (useful for debugging or specific scenarios)
  static loadAllVideos() {
    const videos = document.querySelectorAll('[data-video-lazy-load="true"]');
    const lazyVideo = new LazyVideo();
    videos.forEach(video => {
      if (video.dataset.videoLoaded === 'false') {
        lazyVideo.loadVideo(video);
      }
    });
  }
}

// Auto-initialize
function initLazyVideo() {
  new LazyVideo();
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLazyVideo);
} else {
  // DOM already loaded
  initLazyVideo();
}

// Reinitialize on Shopify section events (for theme editor)
document.addEventListener('shopify:section:load', initLazyVideo);
document.addEventListener('shopify:section:reorder', initLazyVideo);
